import { Box, InputAdornment } from '@mui/material';

import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { PRIMARY_CLIENT_RELATIONSHIP_OPTIONS } from '@/components/form/PersonFormFragment/constants';
import { UsersLeftIcon } from '@/components/icons/UsersLeftIcon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import {
  LEGAL_NAME_LABELS,
  PREFERRED_NAME_HELP_BODY,
  PREFERRED_NAME_LABEL,
} from '@/modules/clientProfiles/clientProfile.constants';
import { StateDropdownInput } from '@/modules/common/StateDropdownInput/StateDropdownInput';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import { ClientSetupFormShape } from './ClientSetup.type';

export interface ClientSetupPrimaryClientFormProps {
  fieldNamePrefix: 'clientOne' | 'clientTwo';
  required: boolean;
}

/**
 * Name / state / relationship fields for a single client. Copied from the
 * create-household modal's CreatePrimaryClientForm, retyped to
 * ClientSetupFormShape.
 */
export function ClientSetupPrimaryClientForm({
  fieldNamePrefix,
  required,
}: ClientSetupPrimaryClientFormProps) {
  const { control } = useFormContext<ClientSetupFormShape>();
  const showRelationshipToPrimary = fieldNamePrefix === 'clientTwo';

  return (
    // Fills the row beside the individual indicator. Without flexGrow the rows
    // below size to their content instead of the available width; minWidth 0
    // lets the grid shrink rather than overflow.
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <FormLayoutRow>
        <FormLayoutItem width={3}>
          <FormAwareTextInput<ClientSetupFormShape>
            control={control}
            label={LEGAL_NAME_LABELS.firstName}
            required={required}
            fieldName={
              `${fieldNamePrefix}.firstName` as FieldNameFromFormShape<ClientSetupFormShape>
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={3}>
          <FormAwareTextInput<ClientSetupFormShape>
            control={control}
            placeholder="Optional"
            label={LEGAL_NAME_LABELS.middleName}
            fieldName={
              `${fieldNamePrefix}.middleName` as FieldNameFromFormShape<ClientSetupFormShape>
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={4}>
          <FormAwareTextInput<ClientSetupFormShape>
            control={control}
            label={LEGAL_NAME_LABELS.lastName}
            required={required}
            fieldName={
              `${fieldNamePrefix}.lastName` as FieldNameFromFormShape<ClientSetupFormShape>
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={2}>
          <FormAwareTextInput<ClientSetupFormShape>
            control={control}
            label="Suffix"
            fieldName={
              `${fieldNamePrefix}.suffix` as FieldNameFromFormShape<ClientSetupFormShape>
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={3}>
          <StateDropdownInput<ClientSetupFormShape>
            label="State"
            required={required}
            fieldName={`${fieldNamePrefix}.stateCode`}
            control={control}
          />
        </FormLayoutItem>
        <FormLayoutItem width={3}>
          <FormAwareTextInput<ClientSetupFormShape>
            control={control}
            placeholder="Optional"
            label={PREFERRED_NAME_LABEL}
            contextualHelp={<PopperContent body={PREFERRED_NAME_HELP_BODY} />}
            fieldName={
              `${fieldNamePrefix}.preferredName` as FieldNameFromFormShape<ClientSetupFormShape>
            }
          />
        </FormLayoutItem>
        {showRelationshipToPrimary && (
          <FormLayoutItem width={3}>
            <FormAwareSelectInput<ClientSetupFormShape>
              control={control}
              label="Relation to primary"
              options={PRIMARY_CLIENT_RELATIONSHIP_OPTIONS}
              fieldName={
                `relationshipToBetweenPrimaryClients` as FieldNameFromFormShape<ClientSetupFormShape>
              }
              startAdornment={
                <InputAdornment position="start">
                  <UsersLeftIcon size={16} />
                </InputAdornment>
              }
            />
          </FormLayoutItem>
        )}
        {showRelationshipToPrimary && (
          <FormLayoutItem
            width={3}
            sx={{ display: 'flex', alignItems: 'center', pt: 3 }}
          >
            <FormAwareSwitch<ClientSetupFormShape>
              control={control}
              label="Deceased"
              labelPosition="right"
              contextualHelp={
                <PopperContent body="When enabled, this individual will not be included in the estate waterfall." />
              }
              fieldName="clientTwo.isDeceased"
            />
          </FormLayoutItem>
        )}
      </FormLayoutRow>
    </Box>
  );
}
